import * as React from 'react';

import cx from 'classnames';

import useRouter from 'js/lib/useRouter';
import user from 'js/lib/user';

import { Grid, PageGridContainer, Tab, TabContext, TabPanel, Tabs } from '@coursera/cds-core';
import type { UserEmail } from '@coursera/grpc-types-useremails/coursera/proto/useremails/v1/email_address';

import HowItWorksInformationLink from 'bundles/enterprise-legacy-learner-home/components/StaticInformationCards/HowItWorksInformationLink';
import ObjectivesInformationCard from 'bundles/enterprise-legacy-learner-home/components/StaticInformationCards/ObjectivesInformationCard';
import MyLearningTabContent from 'bundles/enterprise-legacy-learner-home/components/myLearning/MyLearningTabContent';
import type { OnProductCardClick } from 'bundles/enterprise-legacy-learner-home/types/Products';
import mapExists from 'bundles/enterprise-legacy-learner-home/utils/mapExists';
import EnterpriseHomeMyCoursesTabContent from 'bundles/program-home/components/enterprise-home/EnterpriseHomeMyCoursesTabContent';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import HomeTabContent from 'bundles/program-home/components/enterprise-home/HomeTabContent';
import MultiProgramSkillSetTabContent from 'bundles/program-home/components/enterprise-home/MultiProgramSkillSetTabContent';
import type {
  EnterpriseHomePageQuery_EnterpriseLearnerSummariesV1Resource_byUserIdAndThirdPartyOrgSlug_elements_enterprisePrograms_elements as EnterpriseProgram,
  EnterpriseHomePageQuery_EnterpriseLearnerSummariesV1Resource_byUserIdAndThirdPartyOrgSlug_elements_invitations_elements as ProgramInvitation,
  EnterpriseHomePageQuery_EnterpriseLearnerSummariesV1Resource_byUserIdAndThirdPartyOrgSlug_elements_thirdPartyOrganization as ThirdPartyOrganization,
} from 'bundles/program-home/components/enterprise-home/__generated__/EnterpriseHomePageQuery';

import _t from 'i18n!nls/program-home';

import 'css!./__styles__/EnterpriseHomeTabs';

type PropsFromCaller = {
  isMultiProgram: boolean;
  enterprisePrograms: EnterpriseProgram[];
  programInvitations: ProgramInvitation[];
  userId: number;
  onProductCardClick: OnProductCardClick;
  thirdPartyOrganization: ThirdPartyOrganization;
  thirdPartyOrganizationId: string;
  thirdPartyOrgSlug: string;
  userEmails: UserEmail[];
  allowOrgForCurriculumBuilder?: boolean;
  allowOrgForSpecializationConfiguration?: boolean;
  enableSkillsDashboard: boolean;
  showSkillSetRoleFilter: boolean;
};

export type Props = PropsFromCaller;

export enum TabName {
  Home = 'HOME',
  MyCourses = 'MY_COURSES',
  MyLearning = 'MY_LEARNING',
  SkillSets = 'SKILLSETS',
}

const catalogHelpCardId = 'ProgramHomeCatalogInformationCard';
const objectivesHelpCardId = 'ProgramHomeObjectivesInformationCard';

export function EnterpriseHomeTabs({
  isMultiProgram,
  enterprisePrograms,
  programInvitations,
  onProductCardClick,
  thirdPartyOrganization,
  thirdPartyOrganizationId,
  thirdPartyOrgSlug,
  userId,
  userEmails,
  allowOrgForCurriculumBuilder,
  allowOrgForSpecializationConfiguration,
  showSkillSetRoleFilter,
  enableSkillsDashboard,
}: Props) {
  const router = useRouter();
  const initialTabFromRouter = React.useRef<string | null>();
  const [catalogHelpIsExpanded, setCatalogHelpIsExpanded] = React.useState<boolean>(false);
  const [objectivesHelpIsExpanded, setObjectivesHelpIsExpanded] = React.useState<boolean>(false);

  const hasTSPs = enterprisePrograms.some((program) => program.metadata.linkedTargetSkillProfileIds.length > 0);

  const tabNames: Array<TabName> = React.useMemo(() => {
    const tabNamesArr: Array<TabName> = [TabName.Home];
    if (hasTSPs) {
      tabNamesArr.push(TabName.SkillSets);
    }
    if (enableSkillsDashboard) {
      tabNamesArr.push(TabName.MyLearning);
    } else {
      tabNamesArr.push(TabName.MyCourses);
    }

    return tabNamesArr;
  }, [enableSkillsDashboard, hasTSPs]);

  const selectedIndex = Math.max(0, tabNames.indexOf(router.location.query.currentTab));
  const selectedTab = tabNames[selectedIndex];

  const onTabChange = (nextSelectedTab: string) => {
    if (nextSelectedTab === selectedTab) {
      return;
    }

    router.push({
      ...router.location,
      query: {
        ...router.location.query,
        currentTab: nextSelectedTab,
      },
    });
  };

  React.useEffect(() => {
    initialTabFromRouter.current = router.location.query.currentTab;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    const replaceRoute = () =>
      router.replace({
        ...router.location,
        query: {
          ...router.location.query,
          currentTab: selectedTab,
        },
      });

    const syncTabQuery = () => {
      const currentTab = router.location.query.currentTab;
      const isInvalidTab = !Object.values(TabName).includes(currentTab);
      const tabNamesIncludeInitialTab = tabNames.some((tabName) => tabName === initialTabFromRouter.current);
      const shouldSyncTabs = currentTab !== selectedTab && tabNamesIncludeInitialTab;

      if (shouldSyncTabs || isInvalidTab) {
        replaceRoute();
      }
    };

    syncTabQuery();
  }, [router, selectedTab, tabNames]);

  const toggleCatalogHelpIsExpanded = () => setCatalogHelpIsExpanded((isExpanded) => !isExpanded);
  const toggleObjectivesHelpIsExpanded = () => setObjectivesHelpIsExpanded((isExpanded) => !isExpanded);

  const myCoursesTabContent = (
    <EnterpriseHomeMyCoursesTabContent
      isVisible
      onProductCardClick={onProductCardClick}
      enterprisePrograms={enterprisePrograms}
      thirdPartyOrganization={thirdPartyOrganization}
      userEmails={userEmails}
      userId={userId}
    />
  );

  return (
    <div className={cx('rc-EnterpriseHomeTabs', isMultiProgram && '--multiprogram')} data-testid="enterprise-home-tabs">
      <TabContext value={selectedTab}>
        <div className="tabs-wrapper">
          <PageGridContainer className="tabs-container">
            <Grid item xs={12}>
              <Tabs onChange={onTabChange}>
                {mapExists(tabNames, (tabName) => {
                  switch (tabName) {
                    case TabName.Home:
                      return <Tab key={tabName} label={_t('Home')} value={tabName} />;
                    case TabName.SkillSets:
                      return <Tab key={tabName} label={_t('SkillSets')} value={tabName} />;
                    case TabName.MyCourses:
                      return <Tab key={tabName} label={_t('My Courses')} value={tabName} />;
                    case TabName.MyLearning:
                      return <Tab key={tabName} label={_t('My Learning')} value={tabName} />;
                    default:
                      return null;
                  }
                })}
              </Tabs>
              {selectedTab === TabName.Home && (
                <HowItWorksInformationLink
                  rootClassName="CatalogHelpLink"
                  cardHtmlId={catalogHelpCardId}
                  isOpen={catalogHelpIsExpanded}
                  onClick={toggleCatalogHelpIsExpanded}
                  isCdsEnabled
                />
              )}
              {selectedTab === TabName.SkillSets && (
                <HowItWorksInformationLink
                  rootClassName="CatalogHelpLink"
                  cardHtmlId={objectivesHelpCardId}
                  isOpen={objectivesHelpIsExpanded}
                  onClick={toggleObjectivesHelpIsExpanded}
                  isCdsEnabled
                />
              )}
            </Grid>
          </PageGridContainer>
        </div>
        <div className="tabpanel-container">
          {mapExists(tabNames, (tabName) => {
            const shouldShow = tabName === selectedTab;
            switch (tabName) {
              case TabName.Home:
                return (
                  <TabPanel key={tabName} value={tabName}>
                    <HomeTabContent
                      isVisible={shouldShow}
                      enterprisePrograms={enterprisePrograms}
                      programInvitations={programInvitations}
                      onProductCardClick={onProductCardClick}
                      isAuthenticatedUser={user.isAuthenticatedUser()}
                      hasTSPs={hasTSPs}
                      catalogHelpCardId={catalogHelpCardId}
                      catalogHelpIsExpanded={catalogHelpIsExpanded}
                      toggleCatalogHelpIsExpanded={toggleCatalogHelpIsExpanded}
                      thirdPartyOrganizationId={thirdPartyOrganizationId}
                      thirdPartyOrgSlug={thirdPartyOrgSlug}
                      userId={userId}
                      allowOrgForCurriculumBuilder={allowOrgForCurriculumBuilder}
                      allowOrgForSpecializationConfiguration={allowOrgForSpecializationConfiguration}
                      showSkillSetRoleFilter={showSkillSetRoleFilter}
                    />
                  </TabPanel>
                );
              case TabName.SkillSets:
                return (
                  <TabPanel key={tabName} value={tabName}>
                    <ObjectivesInformationCard
                      rootClassName="m-b-3"
                      id={objectivesHelpCardId}
                      isOpen={objectivesHelpIsExpanded}
                      onRequestClose={toggleObjectivesHelpIsExpanded}
                    />
                    <MultiProgramSkillSetTabContent
                      userId={userId}
                      thirdPartyOrganizationId={thirdPartyOrganizationId}
                      enterprisePrograms={enterprisePrograms}
                    />
                  </TabPanel>
                );
              case TabName.MyCourses:
                return (
                  <TabPanel key={tabName} value={tabName}>
                    {myCoursesTabContent}
                  </TabPanel>
                );
              case TabName.MyLearning:
                return (
                  <TabPanel key={tabName} value={tabName}>
                    <MyLearningTabContent
                      programId={enterprisePrograms[0]?.id}
                      programSlug={enterprisePrograms[0]?.metadata.slug}
                      userId={userId}
                      onProductCardClick={onProductCardClick}
                      myCoursesContent={myCoursesTabContent}
                    />
                  </TabPanel>
                );
              default:
                return null;
            }
          })}
        </div>
      </TabContext>
    </div>
  );
}

export default EnterpriseHomeTabs;
